import React, { useState, useRef, useEffect } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import './WYSIWYGEditor.css';

const WYSIWYGEditor = ({ isOpen, onClose, initialValue = '', onSave, onTypeChange }) => {
  const [content, setContent] = useState(initialValue);
  const [isCodeView, setIsCodeView] = useState(false);
  const [htmlCode, setHtmlCode] = useState(initialValue);
  const [hasUsedCodeView, setHasUsedCodeView] = useState(false);
  const quillRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setContent(initialValue);
      setHtmlCode(initialValue);
      setHasUsedCodeView(false);
    }
  }, [isOpen, initialValue]);

  // Quill modules configuration
  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      [{ 'font': [] }],
      [{ 'size': ['small', false, 'large', 'huge'] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'script': 'sub' }, { 'script': 'super' }],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'indent': '-1' }, { 'indent': '+1' }],
      [{ 'align': [] }],
      ['blockquote', 'code-block'],
      ['link', 'image', 'video'],
      ['clean']
    ],
  };

  const formats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'script',
    'list', 'bullet', 'indent',
    'align',
    'blockquote', 'code-block',
    'link', 'image', 'video'
  ];

  const handleToggleView = () => {
    if (isCodeView) {
      // Switching from code to WYSIWYG - update content from code
      setIsCodeView(false);
      // Delay content update to ensure view switches first
      setTimeout(() => {
        setContent(htmlCode);
      }, 0);
    } else {
      // Switching from WYSIWYG to code - update code from content
      setHtmlCode(content);
      setHasUsedCodeView(true);
      setIsCodeView(true);
    }
  };

  const handleSave = () => {
    const finalContent = isCodeView ? htmlCode : content;
    onSave(finalContent);

    // If user used code view, change type to 'html'
    if (hasUsedCodeView && onTypeChange) {
      onTypeChange('html');
    }

    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  const handleContentChange = (value) => {
    setContent(value);
    if (!isCodeView) {
      setHtmlCode(value);
    }
  };

  const handleCodeChange = (e) => {
    setHtmlCode(e.target.value);
  };

  if (!isOpen) return null;

  return (
    <div className="wysiwyg-overlay">
      <div className="wysiwyg-modal">
        <div className="wysiwyg-header">
          <h3>Rich Text Editor</h3>
          <button className="wysiwyg-close-btn" onClick={handleCancel}>×</button>
        </div>

        <div className="wysiwyg-toolbar-controls">
          <button
            className={`wysiwyg-toggle-btn ${!isCodeView ? 'active' : ''}`}
            onClick={handleToggleView}
            type="button"
          >
            {isCodeView ? 'Switch to WYSIWYG' : 'Switch to HTML Code'}
          </button>
        </div>

        <div className="wysiwyg-content">
          {!isCodeView ? (
            <ReactQuill
              key="wysiwyg-view"
              ref={quillRef}
              theme="snow"
              value={content}
              onChange={handleContentChange}
              modules={modules}
              formats={formats}
              placeholder="Enter your content here..."
            />
          ) : (
            <textarea
              key="code-view"
              className="wysiwyg-code-editor"
              value={htmlCode}
              onChange={handleCodeChange}
              placeholder="Enter HTML code here..."
              spellCheck={false}
            />
          )}
        </div>

        <div className="wysiwyg-footer">
          <button className="wysiwyg-cancel-btn" onClick={handleCancel} type="button">
            Cancel
          </button>
          <button className="wysiwyg-save-btn" onClick={handleSave} type="button">
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default WYSIWYGEditor;
